import { useState, useEffect } from "react";
import "./AlertManagerFormGenerator.styles.scss";

import validator from "@rjsf/validator-ajv8";
import Form from "@rjsf/core";
import { RJSFSchema, FieldProps, CustomValidator } from "@rjsf/utils";

import { ISubValue, IItem, ISchemaConfig } from "./FormGenerator.types";
import { CustomFieldTemplate } from "./CustomFieldTemplate";
import { ErrorListTemplate } from "./ErrorListTemplate";
import { ObjectFieldTemplateWrapper } from "./ObjectFieldTemplate";
import { ArrayFieldItemTemplate } from "./ArrayFieldItemTemplate";
import { AddButton } from "./AddButton";
import AlertDestWithProps from "./AlertDestWrapper";
import {
  FormInput,
  FormCheckbox,
  FormSelect,
  FormMultiSelect,
} from "@/components/Inputs/RJSFInputs";
import DateTimeWidget from "./DateTime";
import { IPortfolioMetadata } from "@pulse-ui/codegen/generated_ts/org_metadata";
import PortfolioMultiSelectWithProps from "./PortfolioMultiSelectWrapper";

function processSubProperties(
  tempSchema: any,
  subProperties: { [key: string]: ISubValue },
  parentKey: string
) {
  for (const subKey in subProperties) {
    const subValue = subProperties[subKey];
    if ("enum" in subValue) {
      tempSchema[parentKey][subKey] = { "ui:widget": FormSelect };
    } else if ("type" in subValue) {
      if (
        subValue.type === "number" ||
        subValue.type === "string" ||
        subValue.type === "integer"
      ) {
        tempSchema[parentKey][subKey] = { "ui:widget": FormInput };
      } else if (subValue.type === "boolean") {
        tempSchema[parentKey][subKey] = { "ui:widget": FormCheckbox };
      }
    }
  }
}

function processItem(tempSchema: any, item: IItem, key: string) {
  if (item.properties) {
    processSubProperties(tempSchema, item.properties, key);
  } else {
    tempSchema[key] = { "ui:widget": FormSelect };
  }
}

interface FormGeneratorProps {
  assetOptions: string[];
  schema: ISchemaConfig;
  handleSubmit: (data: any) => void;
  preset?: any;
  slackChannels: string[];
  telegramChannels: string[];
  smsChannels: string[];
  groupDestinationNames: string[];
  portfolios: string[];
  org_metadata: { portfolios: IPortfolioMetadata[] };
}

const FormGenerator = ({
  assetOptions,
  schema,
  handleSubmit,
  preset,
  slackChannels,
  telegramChannels,
  smsChannels,
  groupDestinationNames,
  portfolios,
  org_metadata,
}: FormGeneratorProps) => {
  const fields_to_exclude = ["config_name"];

  const filteredSchema: ISchemaConfig = {
    ...schema,
    properties: Object.fromEntries(
      Object.entries(schema.properties ?? {}).filter(
        ([k]) => !fields_to_exclude.includes(k)
      )
    ),
    required: (schema.required ?? []).filter(
      (r: string) => !fields_to_exclude.includes(r)
    ),
  };

  const [uiSchema, setUiSchema] = useState<any>("");
  const initialFormData = preset ? preset : {};
  const [formData, setFormData] = useState<{ [key: string]: any }>(
    initialFormData
  );
  const [_, setAssetSymbolIncludeInputValue] = useState<string>("");

  const customValidate: CustomValidator<
    { [key: string]: any },
    RJSFSchema,
    any
  > = (
    formData: { [key: string]: any } | undefined,
    errors: any,
    uiSchema: any
  ) => {
    if (formData) {
      if (uiSchema.assets_include && formData.assets_include) {
        for (let asset of formData.assets_include) {
          if (asset.length < 3) {
            errors.assets_include.addError(
              "Asset symbol must be at least 2 characters long."
            );
          }
        }
      }

      if (
        formData &&
        formData.account &&
        formData.account.length > 1 &&
        formData.portfolio &&
        formData.portfolio.length > 1
      ) {
        errors.account.addError(
          "You cannot select both an account and a portfolio. Please select only one."
        );
        errors.portfolio.addError(
          "You cannot select both an account and a portfolio. Please select only one."
        );
      }

      const selectedFields = [
        "assets_include",
        "assets_exclude",
        "symbols_include",
        "symbols_exclude",
      ].filter((field) => formData[field] && formData[field].length > 0);

      if (selectedFields.length > 1) {
        for (let field of selectedFields) {
          errors[field].addError(
            `You cannot select more than one of the following fields: ${selectedFields.join(
              ", "
            )}.`
          );
        }
      }

      return errors;
    }
  };

  const handleFormDataChange = (formData: any) => {
    if (formData) {
      for (let key in formData) {
        if (typeof formData[key] === "string" && formData[key].length === 0) {
          delete formData[key];
        }
      }

      setFormData(formData);
    }
  };

  useEffect(() => {
    if (filteredSchema) {
      let tempSchema: any = {
        "ui:title": "",
        "ui:submitButtonOptions": {
          props: {
            disabled: false,
            className:
              "pulse-btn--background-500 pulse-color--background-100 pulse-typography-button",
          },
          norender: false,
          submitText: "Submit",
        },
        "ui:order": [
          "name",
          "account",
          "portfolio",
          "additional_portfolios",
          "*",
        ],
      };

      if (filteredSchema.properties) {
        for (const [key, value] of Object.entries(filteredSchema.properties)) {
          tempSchema[key] = {
            "ui:classNames":
              "pulse-typography-body-1 pulse-color--background-100",
            "ui:style": {
              marginBottom: "20px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "flex-start",
              ...(value.type === "boolean" && { marginTop: "0.75rem" }),
            },
          };

          if ("enum" in value) {
            tempSchema[key]["ui:widget"] = FormSelect;
          } else if ("anyOf" in value || "oneOf" in value) {
            if ("oneOf" in value) {
              (value as any).oneOf.unshift({
                const: "",
                title: "Select...",
              });
            }

            if ("anyOf" in value) {
              (value as any).oneOf.unshift({
                const: "",
                title: "Select...",
              });
            }

            (value as any).anyOf?.forEach((item: IItem) =>
              processItem(tempSchema, item, key)
            );
            (value as any).oneOf?.forEach((item: IItem) =>
              processItem(tempSchema, item, key)
            );
          } else if ("type" in value) {
            if (key === "start_time") {
              tempSchema[key]["ui:widget"] = DateTimeWidget;
            } else if (
              value.type === "number" ||
              value.type === "string" ||
              value.type === "integer"
            ) {
              tempSchema[key]["ui:widget"] = FormInput;
            } else if (value.type === "boolean") {
              tempSchema[key]["ui:widget"] = (props: any) => {
                return <FormCheckbox {...props} />;
              };
            } else if (key === "additional_portfolios") {
              tempSchema[key]["ui:field"] = PortfolioMultiSelectWithProps(
                portfolios,
                org_metadata,
                formData
              );
            } else if (
              value.type === "array" &&
              (value as any).items.type === "string"
            ) {
              tempSchema[key]["ui:field"] = "autocomplete";
            } else if (value.type === "object") {
              processSubProperties(tempSchema, (value as any).properties, key);
            } else if (
              value.type === "array" &&
              (value as any).items.type === "object"
            ) {
              tempSchema[key]["items"] = {
                "ui:field": AlertDestWithProps(
                  slackChannels,
                  telegramChannels,
                  smsChannels,
                  groupDestinationNames
                ),
              };
            }
          }
        }
      }

      setUiSchema(tempSchema);
    }
  }, [schema]);

  const fields = {
    autocomplete: (props: FieldProps) => {
      const options = assetOptions.map((value: string) => ({
        label: value,
        value,
      }));
      const handleChange = (newValue: string[]) => {
        setFormData((currentData: any) => ({
          ...currentData,
          [props.name]: newValue,
        }));
      };
      return (
        <FormMultiSelect
          {...props}
          options={options}
          value={formData[props.name] ?? []}
          onChange={handleChange}
          setInputValue={setAssetSymbolIncludeInputValue}
        />
      );
    },
  };

  return (
    <Form
      schema={filteredSchema as RJSFSchema}
      uiSchema={uiSchema}
      fields={fields}
      validator={validator}
      customValidate={customValidate}
      formData={formData}
      templates={{
        FieldTemplate: CustomFieldTemplate,
        ObjectFieldTemplate: ObjectFieldTemplateWrapper,
        ErrorListTemplate: ErrorListTemplate,
        ArrayFieldItemTemplate,
        ButtonTemplates: { AddButton },
      }}
      onChange={(e: any) => handleFormDataChange(e.formData)}
      onSubmit={() => {
        setAssetSymbolIncludeInputValue("");
        handleSubmit(formData);
      }}
    />
  );
};

export default FormGenerator;
